import { useState, useEffect, useCallback } from 'react'
import { reportError } from '@/lib/errorReporter'
import { api } from '@/services/api'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function uint8ArrayToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function usePushSubscription() {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default',
  )

  useEffect(() => {
    if (!('permissions' in navigator)) return
    let status: PermissionStatus | undefined
    const handler = () => setPermission(Notification.permission)

    navigator.permissions.query({ name: 'notifications' }).then((s) => {
      status = s
      s.addEventListener('change', handler)
    })

    return () => {
      status?.removeEventListener('change', handler)
    }
  }, [])

  const subscribe = useCallback(async () => {
    try {
      const result = await Notification.requestPermission()
      setPermission(result)

      if (result !== 'granted') return

      const registration = await navigator.serviceWorker.ready
      const { vapidPublicKey } = await api.getVapidKey()

      let subscription = await registration.pushManager.getSubscription()
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        })
      }

      const key = subscription.getKey('p256dh')
      const auth = subscription.getKey('auth')
      if (!key || !auth) return

      await api.subscribeToPush({
        endpoint: subscription.endpoint,
        p256dh: uint8ArrayToBase64url(key),
        auth: uint8ArrayToBase64url(auth),
      })
    } catch (error) {
      reportError(error, { source: 'usePushSubscription', action: 'subscribe' })
      throw error
    }
  }, [])

  const unsubscribe = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await api.unsubscribeFromPush(subscription.endpoint)
        await subscription.unsubscribe()
      }
    } catch (error) {
      reportError(error, { source: 'usePushSubscription', action: 'unsubscribe' })
    }
  }, [])

  return { permission, subscribe, unsubscribe }
}
